import { z } from "zod";

/**
 * Rede de devs: perfil profissional (títulos, país, redes, experiências, portfólio), seguir e chat.
 * Fonte única das regras: a API valida com estes esquemas; web, desktop e mobile montam os
 * formulários a partir deles.
 */

// ───────────────────────── País ─────────────────────────

/** ISO 3166-1 alfa-2. Nomes vêm de Intl.DisplayNames (no idioma de quem vê). */
export const COUNTRY_CODES = [
  "AD", "AE", "AF", "AG", "AI", "AL", "AM", "AO", "AQ", "AR", "AS", "AT", "AU", "AW", "AX", "AZ",
  "BA", "BB", "BD", "BE", "BF", "BG", "BH", "BI", "BJ", "BL", "BM", "BN", "BO", "BQ", "BR", "BS",
  "BT", "BV", "BW", "BY", "BZ", "CA", "CC", "CD", "CF", "CG", "CH", "CI", "CK", "CL", "CM", "CN",
  "CO", "CR", "CU", "CV", "CW", "CX", "CY", "CZ", "DE", "DJ", "DK", "DM", "DO", "DZ", "EC", "EE",
  "EG", "EH", "ER", "ES", "ET", "FI", "FJ", "FK", "FM", "FO", "FR", "GA", "GB", "GD", "GE", "GF",
  "GG", "GH", "GI", "GL", "GM", "GN", "GP", "GQ", "GR", "GS", "GT", "GU", "GW", "GY", "HK", "HM",
  "HN", "HR", "HT", "HU", "ID", "IE", "IL", "IM", "IN", "IO", "IQ", "IR", "IS", "IT", "JE", "JM",
  "JO", "JP", "KE", "KG", "KH", "KI", "KM", "KN", "KP", "KR", "KW", "KY", "KZ", "LA", "LB", "LC",
  "LI", "LK", "LR", "LS", "LT", "LU", "LV", "LY", "MA", "MC", "MD", "ME", "MF", "MG", "MH", "MK",
  "ML", "MM", "MN", "MO", "MP", "MQ", "MR", "MS", "MT", "MU", "MV", "MW", "MX", "MY", "MZ", "NA",
  "NC", "NE", "NF", "NG", "NI", "NL", "NO", "NP", "NR", "NU", "NZ", "OM", "PA", "PE", "PF", "PG",
  "PH", "PK", "PL", "PM", "PN", "PR", "PS", "PT", "PW", "PY", "QA", "RE", "RO", "RS", "RU", "RW",
  "SA", "SB", "SC", "SD", "SE", "SG", "SH", "SI", "SJ", "SK", "SL", "SM", "SN", "SO", "SR", "SS",
  "ST", "SV", "SX", "SY", "SZ", "TC", "TD", "TF", "TG", "TH", "TJ", "TK", "TL", "TM", "TN", "TO",
  "TR", "TT", "TV", "TW", "TZ", "UA", "UG", "UM", "US", "UY", "UZ", "VA", "VC", "VE", "VG", "VI",
  "VN", "VU", "WF", "WS", "YE", "YT", "ZA", "ZM", "ZW",
] as const; // prettier-ignore
export type CountryCode = (typeof COUNTRY_CODES)[number];
const COUNTRY_SET = new Set<string>(COUNTRY_CODES);
export const isCountryCode = (v: unknown): v is CountryCode => typeof v === "string" && COUNTRY_SET.has(v);

/** Bandeira em emoji a partir do código (indicadores regionais). */
export function countryFlag(code: string | null | undefined): string {
  if (!code || !/^[A-Z]{2}$/.test(code)) return "";
  return String.fromCodePoint(...[...code].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
}

/** Nome do país no idioma pedido (Intl). Sem suporte a Intl.DisplayNames, devolve o código. */
export function countryName(code: string | null | undefined, locale = "pt-BR"): string | null {
  if (!code) return null;
  try {
    return new Intl.DisplayNames([locale], { type: "region" }).of(code) ?? code;
  } catch {
    return code;
  }
}

// ───────────────────────── Títulos ─────────────────────────

/** Sugestões de títulos (a pessoa também pode escrever os próprios). */
export const PROFILE_TITLE_SUGGESTIONS = [
  "Dev FullStack",
  "Dev Front-end",
  "Dev Back-end",
  "Dev Mobile",
  "Java",
  "Python",
  "JavaScript",
  "TypeScript",
  "Go",
  "C#/.NET",
  "PHP",
  "Kotlin",
  "Rust",
  "AppSec",
  "DevSecOps",
  "Pentester",
  "Red Team",
  "Blue Team",
  "DevOps",
  "SRE",
  "Cloud",
  "Engenharia de dados",
  "Ciência de dados",
  "Machine Learning",
  "IA generativa",
  "QA / Testes",
  "UI/UX",
  "Game Dev",
  "Arquitetura de software",
  "Tech Lead",
  "Mentor",
  "Open Source",
  "Estudante",
] as const;

export const MAX_TITLES = 8;
export const ProfileTitle = z
  .string()
  .trim()
  .min(2, "Títulos têm de 2 a 40 caracteres.")
  .max(40, "Títulos têm de 2 a 40 caracteres.")
  .regex(/^[^<>{}\n\r\t]+$/, "Use só texto simples.");

// ───────────────────────── Redes sociais ─────────────────────────

export interface SocialNetwork {
  label: string;
  /** Domínios aceitos (o link precisa ser https e apontar para um deles). */
  hosts: string[];
  placeholder: string;
}

/** GitHub e site continuam nos campos próprios do perfil (githubUrl, websiteUrl). */
export const SOCIAL_NETWORKS = {
  linkedin: { label: "LinkedIn", hosts: ["linkedin.com"], placeholder: "https://www.linkedin.com/in/…" },
  gitlab: { label: "GitLab", hosts: ["gitlab.com"], placeholder: "https://gitlab.com/…" },
  x: { label: "X (Twitter)", hosts: ["x.com", "twitter.com"], placeholder: "https://x.com/…" },
  instagram: { label: "Instagram", hosts: ["instagram.com"], placeholder: "https://instagram.com/…" },
  youtube: { label: "YouTube", hosts: ["youtube.com", "youtu.be"], placeholder: "https://youtube.com/@…" },
  devto: { label: "DEV", hosts: ["dev.to"], placeholder: "https://dev.to/…" },
  medium: { label: "Medium", hosts: ["medium.com"], placeholder: "https://medium.com/@…" },
  stackoverflow: {
    label: "Stack Overflow",
    hosts: ["stackoverflow.com"],
    placeholder: "https://stackoverflow.com/users/…",
  },
  hackerone: { label: "HackerOne", hosts: ["hackerone.com"], placeholder: "https://hackerone.com/…" },
  hackthebox: {
    label: "Hack The Box",
    hosts: ["hackthebox.com", "app.hackthebox.com"],
    placeholder: "https://app.hackthebox.com/profile/…",
  },
  tryhackme: { label: "TryHackMe", hosts: ["tryhackme.com"], placeholder: "https://tryhackme.com/p/…" },
  kaggle: { label: "Kaggle", hosts: ["kaggle.com"], placeholder: "https://www.kaggle.com/…" },
  behance: { label: "Behance", hosts: ["behance.net"], placeholder: "https://www.behance.net/…" },
  dribbble: { label: "Dribbble", hosts: ["dribbble.com"], placeholder: "https://dribbble.com/…" },
  telegram: { label: "Telegram", hosts: ["t.me"], placeholder: "https://t.me/…" },
  discord: {
    label: "Discord",
    hosts: ["discord.com", "discord.gg"],
    placeholder: "https://discord.com/users/…",
  },
} as const satisfies Record<string, SocialNetwork>;
export type SocialNetworkId = keyof typeof SOCIAL_NETWORKS;
export const SOCIAL_NETWORK_IDS = Object.keys(SOCIAL_NETWORKS) as SocialNetworkId[];

/** Link https para um dos domínios da rede (subdomínios valem: www.linkedin.com). */
export function isSocialUrl(network: SocialNetworkId, value: string): boolean {
  let u: URL;
  try {
    u = new URL(value);
  } catch {
    return false;
  }
  if (u.protocol !== "https:" || u.username || u.password) return false;
  const host = u.hostname.toLowerCase();
  return SOCIAL_NETWORKS[network].hosts.some((h) => host === h || host.endsWith(`.${h}`));
}

export const HttpsUrl = z
  .url()
  .max(500)
  .refine((v) => {
    try {
      const u = new URL(v);
      return u.protocol === "https:" && !u.username && !u.password;
    } catch {
      return false;
    }
  }, "Use um link https://");

export const Socials = z
  .record(z.string(), z.string().trim().max(300))
  .superRefine((value, ctx) => {
    for (const [key, url] of Object.entries(value)) {
      if (!(key in SOCIAL_NETWORKS)) {
        ctx.addIssue({ code: "custom", message: `Rede desconhecida: ${key}`, path: [key] });
      } else if (url && !isSocialUrl(key as SocialNetworkId, url)) {
        const net = SOCIAL_NETWORKS[key as SocialNetworkId];
        ctx.addIssue({
          code: "custom",
          message: `Link inválido para ${net.label} (https://${net.hosts[0]}/…)`,
          path: [key],
        });
      }
    }
  })
  // Campos vazios saem do objeto.
  .transform(
    (value) =>
      Object.fromEntries(Object.entries(value).filter(([, url]) => url)) as Partial<
        Record<SocialNetworkId, string>
      >,
  );
export type Socials = z.infer<typeof Socials>;

// ───────────────────────── Experiências ─────────────────────────

export const EXPERIENCE_KINDS = ["work", "education", "certification"] as const;
export const EXPERIENCE_KIND_LABELS: Record<(typeof EXPERIENCE_KINDS)[number], string> = {
  work: "Experiência profissional",
  education: "Formação",
  certification: "Certificação",
};
export const EMPLOYMENT_TYPES = [
  "full_time",
  "part_time",
  "contract",
  "freelance",
  "internship",
  "volunteer",
  "self_employed",
] as const;
export const EMPLOYMENT_TYPE_LABELS: Record<(typeof EMPLOYMENT_TYPES)[number], string> = {
  full_time: "Tempo integral (CLT)",
  part_time: "Meio período",
  contract: "Contrato / PJ",
  freelance: "Freelancer",
  internship: "Estágio",
  volunteer: "Voluntário",
  self_employed: "Autônomo",
};

/** Mês no formato AAAA-MM (o banco guarda o dia 1). */
export const YearMonth = z.string().regex(/^(19[5-9]\d|20\d\d)-(0[1-9]|1[0-2])$/, "Use AAAA-MM.");

export const ExperienceInput = z
  .object({
    kind: z.enum(EXPERIENCE_KINDS).default("work"),
    title: z.string().trim().min(2).max(120),
    organization: z.string().trim().min(2).max(120),
    location: z.string().trim().max(120).nullable().optional(),
    employmentType: z.enum(EMPLOYMENT_TYPES).nullable().optional(),
    start: YearMonth,
    end: YearMonth.nullable().optional(),
    description: z.string().trim().max(2000).nullable().optional(),
    url: HttpsUrl.nullable().optional(),
  })
  .refine((e) => !e.end || e.end >= e.start, {
    message: "O fim não pode ser antes do início.",
    path: ["end"],
  });
export type ExperienceInput = z.infer<typeof ExperienceInput>;
export const MAX_EXPERIENCES = 40;

// ───────────────────────── Portfólio ─────────────────────────

export const PortfolioItemInput = z.object({
  title: z.string().trim().min(2).max(120),
  description: z.string().trim().max(4000).nullable().optional(),
  url: HttpsUrl.nullable().optional(),
  repoUrl: HttpsUrl.nullable().optional(),
  tags: z.array(z.string().trim().min(1).max(30)).max(12).default([]),
  completedOn: YearMonth.nullable().optional(),
  /** Imagens já enviadas (POST /media?purpose=portfolio), na ordem de exibição. */
  mediaIds: z.array(z.uuid()).max(8).default([]),
});
export type PortfolioItemInput = z.infer<typeof PortfolioItemInput>;
export const MAX_PORTFOLIO_ITEMS = 30;

// ───────────────────────── Mídia ─────────────────────────

export const MEDIA_PURPOSES = ["avatar", "portfolio", "chat", "group"] as const;
export type MediaPurpose = (typeof MEDIA_PURPOSES)[number];
export const MEDIA_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif"] as const;
export type MediaType = (typeof MEDIA_TYPES)[number];
export const MEDIA_LIMITS = {
  /** Tamanho máximo por imagem (o front reduz para no máximo `maxSide` antes de enviar). */
  maxBytes: { avatar: 1024 * 1024, portfolio: 4 * 1024 * 1024, chat: 4 * 1024 * 1024, group: 1024 * 1024 },
  maxSide: { avatar: 512, portfolio: 1920, chat: 1920, group: 512 },
  /** Lado máximo aceito pelo servidor (protege quem abre a imagem). */
  maxDimension: 8192,
} as const;
export const mediaUrl = (id: string) => `/api/media/${id}`;

/**
 * Arquivos anexados no chat (documentos, código, PDF, ZIP…): diferentes das imagens, não são
 * redimensionados nem mostrados na conversa — viram um cartão com nome, tipo e tamanho, e baixam
 * como anexo. Executáveis e scripts do sistema não são aceitos.
 */
export const CHAT_FILE_LIMITS = { maxBytes: 8 * 1024 * 1024, maxName: 180 } as const;
export const BLOCKED_FILE_EXTENSIONS = [
  "exe",
  "msi",
  "bat",
  "cmd",
  "com",
  "scr",
  "pif",
  "cpl",
  "ps1",
  "vbs",
  "vbe",
  "wsf",
  "wsh",
  "hta",
  "jse",
  "lnk",
  "reg",
  "dll",
  "sys",
  "apk",
  "app",
  "dmg",
  "pkg",
  "deb",
  "rpm",
  "jar",
] as const;

/** A extensão do arquivo é bloqueada no chat? */
export function isBlockedFileName(name: string): boolean {
  const ext = name.toLowerCase().split(".").pop() ?? "";
  return (BLOCKED_FILE_EXTENSIONS as readonly string[]).includes(ext);
}

/** Nome do arquivo sem caminho nem caracteres de controle (o que vai para o banco e o download). */
export function cleanFileName(name: string): string {
  const base = name.split(/[\\/]/).pop() ?? "";
  // eslint-disable-next-line no-control-regex
  const clean = base.replace(/[\u0000-\u001f\u007f"<>|:*?]/g, "").trim();
  return (clean || "arquivo").slice(0, CHAT_FILE_LIMITS.maxName);
}

// ───────────────────────── Perfil (entrada) ─────────────────────────

export const ProfileExtrasInput = z
  .object({
    country: z.string().trim().toUpperCase().refine(isCountryCode, "País inválido").nullable(),
    titles: z.array(ProfileTitle).max(MAX_TITLES, `Até ${MAX_TITLES} títulos.`),
    about: z.string().trim().max(5000).nullable(),
    socials: Socials,
  })
  .partial();

// ───────────────────────── Chat ─────────────────────────

export const CONVERSATION_KINDS = ["direct", "room", "project", "team"] as const;
export type ConversationKind = (typeof CONVERSATION_KINDS)[number];
export const CHAT_LIMITS = {
  maxMessageChars: 4000,
  maxRoomMembers: 256,
  pageSize: 50,
  /** Editar e apagar a própria mensagem: até este tempo depois do envio. */
  editWindowMs: 15 * 60_000,
} as const;

export const CreateConversationInput = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("direct"), userId: z.uuid() }),
  z.object({
    kind: z.literal("room"),
    title: z.string().trim().min(2).max(80),
    memberIds: z
      .array(z.uuid())
      .min(1)
      .max(CHAT_LIMITS.maxRoomMembers - 1),
  }),
  z.object({ kind: z.literal("project"), projectId: z.uuid() }),
  z.object({ kind: z.literal("team"), groupId: z.uuid() }),
]);
export type CreateConversationInput = z.infer<typeof CreateConversationInput>;

export const SendMessageInput = z
  .object({
    body: z.string().max(CHAT_LIMITS.maxMessageChars).optional(),
    mediaId: z.uuid().optional(),
    replyToId: z.coerce.number().int().positive().optional(),
    /** Id gerado pelo cliente: reenvios não duplicam a mensagem. */
    clientId: z
      .string()
      .regex(/^[A-Za-z0-9_-]{8,64}$/)
      .optional(),
  })
  .refine((m) => (m.body && m.body.trim().length > 0) || m.mediaId, {
    message: "A mensagem está vazia.",
    path: ["body"],
  });
export type SendMessageInput = z.infer<typeof SendMessageInput>;

export const EditMessageInput = z.object({ body: z.string().trim().min(1).max(CHAT_LIMITS.maxMessageChars) });

export const MessagesQuery = z.object({
  /** Mensagens com id menor que este (rolar para cima). */
  before: z.coerce.number().int().positive().optional(),
  /** Mensagens com id maior que este (recuperar o que chegou offline). */
  after: z.coerce.number().int().min(0).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(CHAT_LIMITS.pageSize),
});

export const UpdateConversationInput = z
  .object({
    title: z.string().trim().min(2).max(80),
    muted: z.boolean(),
    archived: z.boolean(),
  })
  .partial();

export const AddMembersInput = z.object({ userIds: z.array(z.uuid()).min(1).max(50) });

export interface ChatPerson {
  id: string;
  handle: string;
  name: string;
  avatarUrl: string | null;
  color: string;
}

export interface ChatMessage {
  id: number;
  conversationId: string;
  sender: ChatPerson | null;
  kind: "text" | "image" | "file" | "system";
  body: string | null;
  mediaUrl: string | null;
  /** Anexo de arquivo (kind "file"): nome, tipo e tamanho. */
  file: { name: string; type: string; size: number } | null;
  replyTo: { id: number; senderName: string | null; body: string | null } | null;
  clientId: string | null;
  createdAt: string;
  editedAt: string | null;
  deletedAt: string | null;
}

export interface ChatConversation {
  id: string;
  kind: ConversationKind;
  title: string;
  /** Para conversas particulares: a outra pessoa. */
  peer: (ChatPerson & { online: boolean; lastSeenAt: string | null }) | null;
  projectId: string | null;
  groupId: string | null;
  members: number;
  myRole: "owner" | "admin" | "member";
  unread: number;
  muted: boolean;
  archived: boolean;
  lastMessage: ChatMessage | null;
  lastMessageAt: string | null;
  lastReadMessageId: number;
}

/** Mensagens do cliente para o servidor pelo WebSocket do chat. */
export const ChatClientEvent = z.discriminatedUnion("type", [
  z.object({ type: z.literal("typing"), conversationId: z.uuid() }),
  z.object({ type: z.literal("ping") }),
]);
export type ChatClientEvent = z.infer<typeof ChatClientEvent>;

/** Eventos do servidor para os clientes conectados. */
export type ChatServerEvent =
  | { type: "ready"; userId: string }
  | { type: "message.new"; message: ChatMessage }
  | { type: "message.updated"; message: ChatMessage }
  /** Mensagem apagada (para todos): some da conversa sem aviso. */
  | { type: "message.removed"; conversationId: string; messageId: number }
  /** Alguém com quem você conversa entrou ou saiu. */
  | { type: "presence"; userId: string; online: boolean; lastSeenAt: string | null }
  | { type: "conversation.updated"; conversationId: string }
  | { type: "conversation.removed"; conversationId: string }
  | { type: "read"; conversationId: string; userId: string; messageId: number }
  | { type: "typing"; conversationId: string; user: ChatPerson }
  | { type: "pong" };

/** Mesma conversa particular para o par, em qualquer ordem. */
export const directKey = (a: string, b: string) => (a < b ? `${a}:${b}` : `${b}:${a}`);

/** Considera online quem deu sinal nos últimos 2 minutos. */
export const ONLINE_WINDOW_MS = 2 * 60_000;
